import { LOAD_MESSAGES_COUNT } from 'store/constant';
import { store } from 'store';
import { ChatUserModel, ChatUserModelOriginal } from 'types/chat-users/chat-user';
import { ChatUsersFiltersRequest } from 'types/chat-users/filters';
import { HistoryMessage, MessageModel } from 'types/chat/messages/message';
import { ChatUserBotStatusType, ChatUserConversation, SendMessageParamsType } from 'types/chat/props';
import { fetcher, toCamelCaseKeys } from 'utils/axios';
import { getCurrentDateFormatted } from 'utils/helpers';
import { mapContactToChatUser } from 'utils/contact-mapper';
import { parseGetContactsPayload } from 'utils/unwrap-get-contacts-response';
import { buildContactSearchTermFilter } from 'utils/contact-search-filters';
import { buildConversationTagCondition } from 'utils/conversation-tag-filter';
import { convertCustomFiltersToApiFilters } from 'utils/convert-filters-to-api';
import { coerceMessageRowTypeField, convertGetMessagesToHistoryMessage, GetMessagesApiRow } from 'utils/convert-messages';
import { logChatReply } from 'utils/debug-chat-reply';
import { parseOutgoingSendError } from 'utils/whatsappErrors';

//* history messages
export async function getChatMessages(pagination: number, phoneNumber: string, count: number | null = null) {
  let result: {
    isActive: boolean | null;
    messages: HistoryMessage[];
    result: boolean;
  } = {
    isActive: null,
    messages: [],
    result: false,
  };
  try {
    const data = await fetcher(
      'get',
      `/api/services/getMessages/${phoneNumber}/${pagination}/${count || LOAD_MESSAGES_COUNT}`,
      'newapi',
    );

    if (data && Array.isArray(data)) {
      const messagesList = data.map(convertGetMessagesToHistoryMessage);
      result = {
        isActive: null,
        messages: messagesList,
        result: true,
      };
    }
  } catch (error) {
    console.warn('getChatMessages error: ', error);
  }

  return result;
}

export async function getChatMessagesFromId(
  id: number,
  phoneNumber: string,
  count: number | null = null,
  type: 'older' | 'newer',
  currentPage: number = 1,
) {
  let result: {
    isActive: boolean | null;
    messages: HistoryMessage[];
    result: boolean;
  } = {
    isActive: null,
    messages: [],
    result: false,
  };

  try {
    let nextPage = currentPage;
    if (type === 'older') {
      nextPage = currentPage + 1; // טעינת עמוד קודם (מספר עמוד גבוה יותר)
    } else if (type === 'newer') {
      nextPage = Math.max(1, currentPage - 1); // טעינת עמוד חדש יותר (מספר עמוד נמוך יותר)
    }

    const data = await fetcher(
      'get',
      `/api/services/getMessages/${phoneNumber}/${nextPage}/${count || LOAD_MESSAGES_COUNT}`,
      'newapi',
    );

    if (data && Array.isArray(data)) {
      const messagesList = data.map(convertGetMessagesToHistoryMessage);
      result = {
        isActive: null,
        messages: messagesList,
        result: true,
      };
    }
  } catch (error) {
    console.log('getChatMessagesFromId error: ', error);
  }

  return result;
}

export async function getChatSearchMessagesArr(phoneNumber: string) {
  let result: HistoryMessage[] = [];
  try {
    const data = await fetcher(
      'get',
      ['/api/whatsapp/searchChatUserWhatsappMessage', { data: { phoneNumberId: phoneNumber } }],
      'history',
    );

    if (data) {
      result = [...data].map((msg) =>
        convertGetMessagesToHistoryMessage(msg as GetMessagesApiRow),
      );
    }
  } catch (error) {
    console.log('searchChatUserWhatsappMessage error: ', error);
  }

  return result;
}

export const getChatMissingHistory = async (
  phoneNumber: string,
  currentMsgs: Array<HistoryMessage | MessageModel>,
) => {
  try {
    let dateFrom,
      dateTo,
      closestBufferIndex = -1;
    if (currentMsgs?.length) {
      closestBufferIndex = currentMsgs.findLastIndex((msg) => msg.FileType === 'buffer');
      const lastMessage = currentMsgs[currentMsgs.length - 1];

      dateTo = lastMessage.DateCreatedOriginal;
      dateFrom = currentMsgs[closestBufferIndex !== -1 ? closestBufferIndex + 1 : 0].DateCreatedOriginal;
    } else {
      const date = getCurrentDateFormatted();
      dateFrom = date;
      dateTo = date;
    }

    const filteredMessages =
      closestBufferIndex !== -1 ? currentMsgs.slice(closestBufferIndex + 1) : currentMsgs;
    const data = await fetcher(
      'post',
      [
        '/api/sendMessage/getMissingMessages',
        {
          data: {
            date: dateFrom,
            dateTo: dateTo,
            ids: filteredMessages.map((msg) => msg.Id),
            chatUserPhoneNumberId: phoneNumber.startsWith('972') ? '0' + phoneNumber.slice(3) : phoneNumber,
          },
        },
      ],
      'history',
    );

    if (data?.messages) {
      return (data.messages as Array<HistoryMessage | MessageModel>).map((msg) =>
        coerceMessageRowTypeField(msg),
      );
    } else {
      return [];
    }
  } catch (error) {
    console.log('getChatUpdateHistory err: ', error);
    return [];
  }
};

export const fetchGetFavouriteMessages = async (page: number, number: number) => {
  try {
    const data = await fetcher(
      'get',
      ['/api/massMessaging/getFavouriteMessages', { data: { page, number } }],
      'history',
    );
    if (data) {
      return data as {
        result: boolean;
        faivourites: Array<{
          message: HistoryMessage | MessageModel;
          chatUser?: ChatUserModelOriginal | null;
          file?: string | null;
        }>;
      };
    } else return null;
  } catch (error) {
    console.warn('fetchGetFavouriteMessages error: ', error);
    return null;
  }
};

export const fetchRemoveFromFavourites = async (id: number) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/updateMessageFavouriteStatus',
        {
          data: {
            id,
            isFavourite: false,
          },
        },
      ],
      'history',
    );
    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('fetchRemoveFromFavourites error: ', error);
    return null;
  }
};

export type SendOutgoingMessageResult = {
  result: boolean;
  /** DB / server message id when returned */
  messageId?: number;
  errorCode?: string | number;
};

//* Send message
export async function sendChatMessage(params: SendMessageParamsType): Promise<SendOutgoingMessageResult> {
  if (params.replyMessageId != null && params.replyMessageId !== '') {
    logChatReply('api:SendOutgoingMessage request', {
      messageType: params.messageType,
      replyMessageId: params.replyMessageId,
      tempId: params.tempId,
      chatPhone: params.id,
      messageLen: params.message?.length ?? 0,
    });
  }
  try {
    const data = (await fetcher(
      'post',
      ['/api/whatsapp/SendOutgoingMessage', { data: params }],
      'messages',
    )) as Record<string, unknown> | null | undefined;

    if (data && typeof data === 'object') {
      const messageIdRaw = data.messageId ?? data.MessageId ?? data.id ?? data.Id;
      const messageId =
        typeof messageIdRaw === 'number'
          ? messageIdRaw
          : typeof messageIdRaw === 'string'
            ? Number.parseInt(messageIdRaw, 10)
            : undefined;
      const errorCode =
        data.errorCode ?? data.ErrorCode ?? data.metaErrorCode ?? data.code ?? data.Code;
      // messages API has no camelCase response interceptor — .NET may return Result/MessageId (PascalCase).
      const resultFlag = data.result ?? data.Result;
      const ok =
        resultFlag === true ||
        resultFlag === 1 ||
        (typeof resultFlag === 'string' && ['true', '1'].includes(resultFlag.toLowerCase()));
      const out = {
        result: ok,
        messageId: Number.isFinite(messageId as number) ? (messageId as number) : undefined,
        errorCode:
          typeof errorCode === 'number' || typeof errorCode === 'string' ? errorCode : undefined,
      };
      if (params.replyMessageId != null && params.replyMessageId !== '') {
        logChatReply('api:SendOutgoingMessage response', { ...out, rawKeys: data ? Object.keys(data) : [] });
      }
      return out;
    }
    if (params.replyMessageId != null && params.replyMessageId !== '') {
      logChatReply('api:SendOutgoingMessage empty body', { result: false });
    }
    return { result: false };
  } catch (error) {
    console.log('sendChatTextMessage error: ', error);
    const p = parseOutgoingSendError(error);
    const errorCode = p.errorCode ?? (p.httpStatus === 500 ? 500 : undefined);
    if (params.replyMessageId != null && params.replyMessageId !== '') {
      logChatReply('api:SendOutgoingMessage catch', { errorCode, error: String(error) });
    }
    return { result: false, errorCode };
  }
}

export async function sendChatFileMessage(formData: FormData) {
  try {
    const data = await fetcher(
      'post',
      ['/api/whatsapp/SendOutgoingMessageWithFile', { data: formData }],
      'messages',
    );

    if (data) {
      return data;
    }
    return false;
  } catch (error) {
    console.log('sendChatFileMessage error: ', error);
    throw error;
  }
}

//* Messages common
export async function fetchSetMessageFavourite(id: number, state: boolean) {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/updateMessageFavouriteStatus',
        {
          data: {
            id,
            isFavourite: state,
          },
        },
      ],
      'history',
    );

    return data;
  } catch (error) {
    console.warn('setMessageFavourite error: ', error);
    return null;
  }
}

//* Chat users
export async function fetchGetChatUsers(params: ChatUsersFiltersRequest) {
  try {
    // בניית מבנה הפילטרים החדש
    const filters: any[] = [];
    const conditions: any[] = [];

    // פילטר isOpen - משתמש ב-lastSession.status
    if (params.filter_by_open === 'open') {
      conditions.push({
        field: 'lastSession.status',
        operator: 'eq',
        value: 'open',
      });
    } else if (params.filter_by_open === 'close') {
      conditions.push({
        field: 'lastSession.status',
        operator: 'eq',
        value: 'closed',
      });
    }
    // אם 'all' - לא נוסיף פילטר

    // פילטר unreadCount
    if (params.isOnlyUnread) {
      conditions.push({
        field: 'unreadCount',
        operator: 'gt',
        value: 0,
      });
    }

    // פילטר תגיות שיחה (SessionConversationTag) — תו יחיד ב-conditions; מרובות כ-OR בקבוצה נפרדת.
    // הטוקנים נשארים בפורמט הישן ('noLabel' | JSON.stringify(ChatUserLabel)) לתאימות ל-userConfig
    // שמור; ההמרה לשדה conversationTag של מנוע הסינון בנוד נעשית כאן בלבד.
    const labelTokens: string[] = [];
    if (params.labels && params.labels.length > 0) {
      labelTokens.push(...params.labels);
    } else if (params.label && params.label !== '') {
      labelTokens.push(params.label);
    }
    let labelOrFilterGroup: { logic: 'or'; conditions: any[] } | null = null;
    if (labelTokens.length === 1) {
      conditions.push(buildConversationTagCondition(labelTokens[0]));
    } else if (labelTokens.length > 1) {
      labelOrFilterGroup = {
        logic: 'or',
        conditions: labelTokens.map((t) => buildConversationTagCondition(t)),
      };
    }

    // פילטר search_term — חיפוש רחב בשדות שם וטלפון של איש קשר
    const contactSearchFilter = buildContactSearchTermFilter(params.search_term ?? '');
    if (contactSearchFilter) {
      filters.push(contactSearchFilter);
    }

    // פילטר isBot — דווקא istrue ולא isnotnull: בוט שסיים משאיר isBot=0 (לא NULL),
    // ו-isnotnull היה תופס כל שיחה שאי פעם רץ בה בוט.
    // הכרעת מוצר 8.10: שיחת בוט מופיעה בכל התצוגות לפי הסטטוס שלה — אין החרגה;
    // תצוגת «בוטים» היא עדשה נוספת בלבד.
    if (params.is_bot) {
      conditions.push({
        field: 'activeBot',
        operator: 'istrue',
      });
    }

    // פילטר assigneeFilter - עכשיו נשתמש ב-operators לפי התצוגה
    // במקום לשלוח רשימה של UDID, נשתמש ב-operators כמו ne, eq, isnull, isnotnull
    // משתמש ב-managerId במקום assigneeId
    
    // אם assigneeFilter מכיל 'my' - זה אומר "שיחות שלי"
    if (params.assigneeFilter && params.assigneeFilter.length > 0) {
      if (params.assigneeFilter.includes('my')) {
        // "שיחות שלי" - נשלח managerId eq <currentUser.Udid>
        const currentUser = store.getState().user;
        if (currentUser?.Udid) {
          conditions.push({
            field: 'managerId',
            operator: 'eq',
            value: currentUser.Udid,
          });
        }
      } else if (params.assigneeFilter.includes('notMy')) {
        // "שיחות של אחרים" - נשלח managerId ne <currentUser.Udid> AND managerId isnotnull
        const currentUser = store.getState().user;
        if (currentUser?.Udid) {
          conditions.push({
            field: 'managerId',
            operator: 'ne',
            value: currentUser.Udid,
          });
          conditions.push({
            field: 'managerId',
            operator: 'isnotnull',
          });
        }
      } else if (params.assigneeFilter.includes('no-assign')) {
        // "לא משויכות" - נשלח managerId isnull
        conditions.push({
          field: 'managerId',
          operator: 'isnull',
        });
      } else {
        // אם זה רשימה של UDID (למקרים מיוחדים), נשתמש ב-in
        conditions.push({
          field: 'managerId',
          operator: 'in',
          value: params.assigneeFilter,
        });
      }
    }

    if (params.flowStatusCode) {
      conditions.push({
        field: 'flowStatusCode',
        operator: 'eq',
        value: params.flowStatusCode,
      });
    }

    // פילטר: רק אנשי קשר שיש להם lastMessageObject (לא במצב «חסומים בלבד»).
    // מסך אנשי הקשר מעביר includeContactsWithoutMessages — שם מציגים את כולם,
    // כולל אנשי קשר שנוצרו ב-API/ייבוא וטרם הוחלפה איתם הודעה.
    if (!params.onlyMessagingBlocked && !params.includeContactsWithoutMessages) {
      conditions.push({
        field: 'lastMessageObject.id',
        operator: 'isnotnull',
      });
    }

    if (params.onlyMessagingBlocked) {
      conditions.push({
        field: 'isMessagingBlocked',
        operator: 'eq',
        value: true,
      });
    }

    // הוסף את כל ה-conditions ל-filters אם יש כאלה
    if (conditions.length > 0) {
      filters.push({
        conditions: conditions,
      });
    }
    if (labelOrFilterGroup) {
      filters.push(labelOrFilterGroup);
    }

    // הוסף פילטרים מותאמים (custom filters) אם יש
    if (params.customFilters && params.customFilters.length > 0) {
      const customApiFilters = convertCustomFiltersToApiFilters(
        params.customFilters,
        params.customFiltersLogic,
      );
      filters.push(...customApiFilters);
    }

    // בניית ה-body המלא
    const orderByField =
      params.order_by === 'snooze_end_date' || params.flowStatusCode === 5
        ? 'lastSession.snoozeEndTime'
        : 'lastMessageObject.dateCreated';
    const requestBody: any = {
      filters: filters.length > 0 ? filters : undefined,
      limit: params.page_number || 50,
      offset: params.page_offset || 0,
      orderBy: orderByField,
      orderDirection: params.order_descending ? 'desc' : 'asc',
    };

    // הסר שדות undefined
    Object.keys(requestBody).forEach((key) => {
      if (requestBody[key] === undefined) {
        delete requestBody[key];
      }
    });

    // קריאה ל-API החדש עם הפילטרים
    const raw = await fetcher('post', ['/api/services/getContacts', { data: requestBody }], 'servernode');
    // servernode מחזיר PascalCase (ללא interceptor) — isWhatsappWindowOpen / lastIncomingMessage חייבים camelCase ל-mapper.
    const parsed = parseGetContactsPayload(toCamelCaseKeys(raw));
    if (!parsed) {
      return null;
    }

    const { contacts, totalCount } = parsed;
    const chatUsers = contacts.map(mapContactToChatUser);

    return {
      result: true,
      totalCount,
      rawContacts: contacts,
      allChatUsers: {
        result: true,
        chatUsers,
      },
      myChatUsers: {
        result: true,
        chatUsers,
      },
    };
  } catch (error) {
    console.warn('fetchGetChatUsers error: ', error);
    return null;
  }
}

/**
 * שליפת איש קשר בודד דרך הנוד (getContacts) — מחזיר ChatUserModel מלא כולל
 * SessionId, flowStatusCode ו-ConversationTags (שדות שנגזרים מ-lastSession).
 * המסלול ה-legacy (/contacts/getLegasyByPhone ב-C#) לא מחזיר אותם — משתמש
 * שנטען דרכו נכנס ל-Redux בלי מזהה סשן ובלי תגיות שיחה.
 */
export async function fetchChatUserByPhoneWeb3(phone: string): Promise<ChatUserModel | null> {
  try {
    const requestBody = {
      filters: [{ conditions: [{ field: 'phone', operator: 'eq', value: phone }] }],
      limit: 1,
      offset: 0,
    };
    const raw = await fetcher('post', ['/api/services/getContacts', { data: requestBody }], 'servernode');
    const parsed = parseGetContactsPayload(toCamelCaseKeys(raw));
    const contact = parsed?.contacts?.[0];
    if (!contact) return null;
    return mapContactToChatUser(contact);
  } catch (error) {
    console.warn('fetchChatUserByPhoneWeb3 error: ', error);
    return null;
  }
}

export const fetchGetBotStatus = async (phone: string) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/services/getChatUserBotStatus',
        {
          data: {
            chatUserPhoneId: phone,
          },
        },
      ],
      'history',
    );
    if (data) {
      return data as ChatUserBotStatusType;
    } else return null;
  } catch (error) {
    console.warn('getChatUserMeta error: ', error);
    return null;
  }
};

export const fetchStopUserBots = async (phone: string, botId: number) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/stopUserBots',
        {
          data: {
            userPhoneNumberId: phone,
            botId,
          },
        },
      ],
      'history',
    );
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('fetchStopUserBots error: ', error);
    return null;
  }
};

export const fetchGetChatUserConversations = async (phone: string) => {
  try {
    const data = await fetcher('get', `/api/services/getChatUserConversations/${phone}/1/10`, 'history');
    if (data) return data as { result: boolean; readyLogsList: Array<ChatUserConversation> };
    else return null;
  } catch (error) {
    console.warn('fetchGetChatUserConversations error: ', error);
    return null;
  }
};

export const fetchUpdateCovnersationLog = async (
  id: number,
  name: string | null,
  priority: number | null,
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/updateCovnersationLog',
        {
          data: {
            id,
            name,
            priority,
          },
        },
      ],
      'history',
    );
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('fetchUpdateCovnersationLog error: ', error);
    return null;
  }
};

export const fetchForwardMessagesToChatUsers = async (
  messagesList: number[],
  chatUsersPhoneList: string[],
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/forwardMessage',
        {
          data: {
            messagesList,
            chatUsersPhoneList,
          },
        },
      ],
      'messages',
    );
    if (data) return data as { result: boolean; sent?: number; failed?: number; skipped?: number };
    else return null;
  } catch (error) {
    console.warn('fetchForwardMessagesToChatUsers error: ', error);
    return null;
  }
};
